import React, { useState } from 'react';
import { Scissors, Sparkles, Loader2, Plus, Trash2, Calculator } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

let _rowSeq = 0;
const rid = () => `row-${++_rowSeq}`;

export default function Optimasi() {
  const [desc, setDesc] = useState('');
  const [rows, setRows] = useState([{ _id: rid(), nama: '', panjang: '', qty: '' }]);
  const [barLength, setBarLength] = useState(6);
  const [kerf, setKerf] = useState(3);
  const [result, setResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);

  const updateRow = (i, f, v) => setRows((r) => r.map((x, idx) => idx === i ? { ...x, [f]: v } : x));
  const addRow = () => setRows((r) => [...r, { _id: rid(), nama: '', panjang: '', qty: '' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const parseAI = async () => {
    if (!desc.trim()) { toast.error('Tulis deskripsi pekerjaan dulu'); return; }
    setAiLoading(true);
    try {
      const r = await api.post('/ai/cutting-parse', { deskripsi: desc });
      const reqs = (r.data.requirements || []).map((x) => ({ _id: rid(), nama: x.nama || '', panjang: x.panjang ?? '', qty: x.qty ?? '' }));
      if (reqs.length) { setRows(reqs); toast.success(`AI menemukan ${reqs.length} jenis potongan`); }
      else toast.info('AI tidak menemukan potongan. Isi manual ya.');
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'AI gagal membaca'); } finally { setAiLoading(false); }
  };

  const calculate = async () => {
    const reqs = rows.filter((r) => r.nama && Number(r.panjang) > 0 && Number(r.qty) > 0)
      .map((r) => ({ nama: r.nama, panjang: Number(r.panjang), qty: Number(r.qty) }));
    if (reqs.length === 0) { toast.error('Isi minimal 1 baris potongan yang valid'); return; }
    setCalcLoading(true);
    try {
      const r = await api.post('/tools/cutting', { requirements: reqs, bar_length: Number(barLength) || 6, kerf_mm: Number(kerf) || 0 });
      setResult(r.data);
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal menghitung'); } finally { setCalcLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-xl font-bold"><Scissors className="h-5 w-5 text-primary" /> Optimasi Potong Batang</h1>
        <p className="text-sm text-muted-foreground">Hitung jumlah batang & minimalkan sisa (waste) aluminum.</p>
      </div>

      <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Baca otomatis dari deskripsi (AI)</div>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="cth: Jendela sliding 1.5m x 1.2m sebanyak 3 unit pakai Sliding 3 inch YKK" data-testid="optimasi-ai-deskripsi" />
        <Button onClick={parseAI} disabled={aiLoading} className="mt-2 rounded-xl" data-testid="optimasi-ai-baca">
          {aiLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Baca dengan AI
        </Button>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold">Daftar Potongan</h2>
          <Button variant="outline" size="sm" onClick={addRow} className="rounded-lg"><Plus className="mr-1 h-4 w-4" /> Baris</Button>
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r._id} className="grid grid-cols-12 items-end gap-2">
              <div className="col-span-6"><Label className="text-[10px] text-muted-foreground">Nama Profil</Label><Input value={r.nama} onChange={(e) => updateRow(i, 'nama', e.target.value)} placeholder="Sliding 3 inch" className="h-9" data-testid="optimasi-row-nama" /></div>
              <div className="col-span-3"><Label className="text-[10px] text-muted-foreground">Panjang (m)</Label><Input type="number" value={r.panjang} onChange={(e) => updateRow(i, 'panjang', e.target.value)} placeholder="1.5" className="h-9" /></div>
              <div className="col-span-2"><Label className="text-[10px] text-muted-foreground">Qty</Label><Input type="number" value={r.qty} onChange={(e) => updateRow(i, 'qty', e.target.value)} placeholder="6" className="h-9" /></div>
              <div className="col-span-1"><button onClick={() => removeRow(i)} className="flex h-9 w-full items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button></div>
            </div>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Panjang Batang (m)</Label><Input type="number" value={barLength} onChange={(e) => setBarLength(e.target.value)} className="h-9" /></div>
          <div className="space-y-1.5"><Label>Mata Gergaji / Kerf (mm)</Label><Input type="number" value={kerf} onChange={(e) => setKerf(e.target.value)} className="h-9" /></div>
        </div>
        <Button onClick={calculate} disabled={calcLoading} className="mt-3 w-full rounded-xl" data-testid="optimasi-hitung">
          {calcLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />} Hitung Optimasi
        </Button>
      </div>

      {result && (
        <div className="space-y-3" data-testid="optimasi-hasil">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border bg-card p-4"><div className="text-xs text-muted-foreground">Total Batang Dibutuhkan</div><div className="font-display text-2xl font-bold text-primary">{result.total_bars}</div></div>
            <div className="rounded-xl border bg-card p-4"><div className="text-xs text-muted-foreground">Rata-rata Sisa (Waste)</div><div className={cn('font-display text-2xl font-bold', result.avg_waste_pct > 20 ? 'text-orange-600' : 'text-emerald-600')}>{result.avg_waste_pct}%</div></div>
          </div>
          {result.plans.map((p, idx) => (
            <div key={idx} className="rounded-xl border bg-card p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold">{p.nama}</div>
                <div className="text-sm text-muted-foreground">{p.total_bars} batang · waste {p.waste_pct}%</div>
              </div>
              {p.over_length?.length > 0 && (
                <div className="mt-2 rounded-lg bg-orange-50 px-2 py-1.5 text-xs text-orange-800">Ada potongan lebih panjang dari batang ({p.over_length.join(', ')} m) — perlu sambungan.</div>
              )}
              <div className="mt-3 space-y-2">
                {p.bars.map((b) => {
                  let pos = 0;
                  return (
                    <div key={b.index}>
                      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground"><span>Batang {b.index}</span><span>Sisa {b.sisa} m</span></div>
                      <div className="relative flex h-7 w-full overflow-hidden rounded-md border bg-muted">
                        {b.pieces.map((pc, pi) => {
                          const w = (pc / p.bar_length) * 100;
                          const colors = ['bg-primary/80', 'bg-teal-500/80', 'bg-sky-500/80', 'bg-amber-500/80', 'bg-emerald-500/80'];
                          const el = <div key={pi} className={cn('flex items-center justify-center border-r border-white/40 text-[10px] font-medium text-white', colors[pi % colors.length])} style={{ width: `${w}%` }} title={`${pc} m`}>{w > 12 ? `${pc}m` : ''}</div>;
                          pos += pc;
                          return el;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
